/**
 * Silver Sage Financial Ledger — shared client and vocabulary.
 *
 * This is the ONLY module the frontend uses for bookkeeping figures. Every
 * surface (Financial Ledger, Reports, Payroll, Executive Desk, Operations
 * Home, class profitability, Grace report, MAVIS) reads these helpers so the
 * same metric for the same date range is identical everywhere.
 *
 * Accounting vocabulary — use these words exactly, never interchangeably:
 *   Actual Revenue          Qualifying money successfully collected.
 *   Actual Expenses         Money that has actually been paid out.
 *   Net Cash Activity       Actual money received minus actual money paid out.
 *   Outstanding Obligations Known expenses not yet paid (payroll, Grace accrual).
 *   Projected Revenue       Future expected registration income, not collected.
 *   Projected Net           Actual + expected revenue − actual + expected costs.
 */
import { supabase } from "@/integrations/supabase/client";

export const FINANCIAL_START_DATE = "2026-07-01";

export type LedgerDirection = "in" | "out" | "transfer";

export type LedgerStatus =
  | "pending" | "completed" | "paid" | "refunded" | "partially_refunded"
  | "voided" | "reversed" | "failed" | "projected";

export type PaymentMethod =
  | "zelle" | "cash" | "check" | "debit_card" | "credit_card" | "ach" | "stripe" | "other";

export const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "debit_card", label: "Debit Card" },
  { value: "credit_card", label: "Credit Card" },
  { value: "ach", label: "ACH" },
  { value: "stripe", label: "Stripe" },
  { value: "other", label: "Other" },
];

export const MONEY_OUT_CATEGORIES = [
  "Expense",
  "Supply Purchase",
  "Refund",
  "Reimbursement",
  "Payroll Adjustment",
  "Facility Payment",
  "Grace Community Payment",
  "Contractor or Service Payment",
  "Bank Fee",
  "Software Subscription",
  "Marketing Expense",
  "Event Expense",
  "Other Expense",
] as const;

export const MONEY_IN_CATEGORIES = [
  "Income Adjustment",
  "Other Income",
] as const;

export const PERIOD_OPTIONS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "current_semester", label: "Current semester" },
  { value: "previous_semester", label: "Previous semester" },
  { value: "year_to_date", label: "Year to date" },
  { value: "all_time", label: `All time since ${FINANCIAL_START_DATE}` },
  { value: "custom", label: "Custom date range" },
] as const;

export interface LedgerEntry {
  id: string;
  /** Permanent, never-reused transaction ID, e.g. SSH-2026-000031. */
  transaction_ref: string | null;
  direction: LedgerDirection;
  category: string;
  status: LedgerStatus;
  amount_cents: number;
  transaction_date: string;
  posted_date: string | null;
  description: string;
  payment_method: PaymentMethod | null;
  counterparty_name: string | null;
  notes: string | null;
  entry_kind: "automatic" | "manual" | "system_import" | "opening_balance" | "reversal" | "adjustment";
  source_type: string;
  source_id: string | null;
  reverses_entry_id: string | null;
  related_class_id: string | null;
  related_class_title: string | null;
  related_instructor_id: string | null;
  related_registration_id: string | null;
  related_event_id: string | null;
  student_name: string | null;
  receipt_url: string | null;
  documentation_state: "attached" | "missing" | "not_required";
  reference_number: string | null;
  metadata: Record<string, unknown>;
  created_by_label: string | null;
  voided_at: string | null;
  voided_reason: string | null;
  reconciled_at: string | null;
  created_at: string;
}

export interface LedgerPeriod {
  key: string;
  label: string;
  start_date: string;
  end_date: string;
}

export interface LedgerSummary {
  actual_revenue_cents: number;
  actual_expenses_cents: number;
  net_cash_activity_cents: number;
  payroll_paid_cents: number;
  payroll_outstanding_cents: number;
  grace_accrued_cents: number;
  grace_paid_cents: number;
  grace_outstanding_cents: number;
  refunds_cents: number;
  supplies_cents: number;
  other_expenses_cents: number;
  outstanding_obligations_cents: number;
  projected_revenue_cents: number;
  projected_net_cents: number;
  transaction_count: number;
  unreconciled_count: number;
  missing_documentation_count: number;
}

export interface PayrollForecastInstructor {
  instructor_name: string;
  amount_cents: number;
  meeting_count: number | null;
  payment_method: string | null;
  schedule_label: string | null;
  source: string;
}

export interface PayrollForecastPayDate {
  pay_date: string;
  amount_cents: number;
  instructors: PayrollForecastInstructor[];
}

export interface PayrollForecast {
  remaining_cents: number;
  pay_dates: PayrollForecastPayDate[];
  instructor_count: number;
  needs_review: Array<{ instructor_name?: string; reason?: string }>;
  basis: string;
}

export interface ProjectedNetLine {
  label: string;
  amount_cents: number;
  sign: "+" | "-";
}

export interface LedgerSummaryResponse {
  ok: true;
  period: LedgerPeriod;
  summary: LedgerSummary;
  payroll_forecast?: PayrollForecast;
  projected_net_breakdown?: ProjectedNetLine[];
  definitions?: Record<string, string>;
  source_counts?: Record<string, number>;
  opening_balance: OpeningBalance | null;
  reconciled_through: string | null;
}


export interface OpeningBalance {
  id: string;
  as_of_date: string;
  bank_cents: number;
  cash_on_hand_cents: number;
  notes: string | null;
  confirmed_by_label: string | null;
  confirmed_at: string;
}

export interface SyncSection {
  source: string;
  dry_run: boolean;
  found: number;
  entries_created: number;
  entries_pending: number;
  created: Array<{ id: string; label: string; amount_cents?: number; date?: string }>;
  matched: Array<{ id: string; label: string; amount_cents?: number; date?: string }>;
  excluded: Array<{ id: string; label: string; reason: string }>;
  refunds_found?: Array<{ id: string; label: string; amount_cents: number }>;
  needs_review: Array<{ id: string; label: string; reason: string }>;
}

export interface SyncResult {
  ok: true;
  dry_run: boolean;
  registrations: SyncSection;
  payroll: SyncSection;
  grace: SyncSection;
}

export interface ReconcileResult {
  ok: true;
  period_start: string;
  period_end: string;
  discrepancy_count: number;
  reconciled_through: string | null;
  findings: Record<string, any[]>;
}

async function invoke<T>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("financial-ledger", {
    body: { action, ...payload },
  });
  if (error) throw new Error(data?.error ?? error.message);
  if (data && data.ok === false) throw new Error(data.error ?? "Financial ledger request failed");
  return data as T;
}

export const getLedgerSummary = (period: string, range?: { start_date?: string; end_date?: string }) =>
  invoke<LedgerSummaryResponse>("summary", { period, ...range });

export const listLedgerEntries = (
  period: string,
  filters: Record<string, unknown> = {},
  range?: { start_date?: string; end_date?: string },
) => invoke<{ ok: true; period: LedgerPeriod; rows: LedgerEntry[] }>("list", { period, filters, ...range });

export const listLedgerCategories = () =>
  invoke<{ ok: true; categories: string[] }>("categories");

export const addManualTransaction = (transaction: Record<string, unknown>) =>
  invoke<{ ok: true; entry: LedgerEntry }>("add_manual", { transaction });

export const attachReceipt = (entry_id: string, receipt_url: string) =>
  invoke<{ ok: true }>("attach_receipt", { entry_id, receipt_url });

export const reverseTransaction = (entry_id: string, reason: string, reversal_date?: string) =>
  invoke<{ ok: true; reversal: LedgerEntry }>("reverse", { entry_id, reason, reversal_date });

export const adjustTransaction = (entry_id: string, delta_cents: number, reason: string, adjustment_date?: string) =>
  invoke<{ ok: true; adjustment: LedgerEntry }>("adjust", { entry_id, delta_cents, reason, adjustment_date });

export const syncLedger = (dryRun = true) => invoke<SyncResult>("sync", { dry_run: dryRun });

export const reconcileLedger = (range: { start_date?: string; end_date?: string; persist?: boolean } = {}) =>
  invoke<ReconcileResult>("reconcile", range);

export const getEntryAudit = (entry_id: string) =>
  invoke<{ ok: true; rows: any[] }>("audit", { entry_id });

export const getOpeningBalance = () =>
  invoke<{ ok: true; opening_balance: OpeningBalance | null }>("opening_balance_get");

export const setOpeningBalance = (payload: {
  as_of_date?: string; bank_cents: number; cash_on_hand_cents: number; notes?: string;
}) => invoke<{ ok: true; opening_balance: OpeningBalance }>("opening_balance_set", { ...payload, confirmed: true });


// ------------------------------------------------- financial integrity lockdown
export type HealthIndicator = "green" | "yellow" | "red";

export interface IntegrityCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string;
}

export interface FinancialHealth {
  ok: true;
  status: "healthy" | "needs_review" | "reconciliation_failed";
  label: string;
  indicator: HealthIndicator;
  last_reconciled_at: string | null;
  reconciled_through: string | null;
  unresolved_count: number;
  counts: {
    duplicate_transactions: number;
    missing_ledger_entries: number;
    missing_payroll_links: number;
    missing_payment_links: number;
  };
  checks: IntegrityCheck[];
  findings: Record<string, any[]>;
  totals: Record<string, number> | null;
}

export interface PeriodClosure {
  id: string;
  period_start: string;
  period_end: string;
  period_label: string;
  status: "closed" | "reopened";
  totals: Record<string, any>;
  reconciled_at: string | null;
  discrepancy_count: number;
  closed_by_label: string | null;
  closed_at: string;
  reopened_at: string | null;
  reopened_by_label: string | null;
  notes: string | null;
}

export interface DailySnapshot {
  id: string;
  snapshot_date: string;
  actual_revenue_cents: number;
  actual_expenses_cents: number;
  payroll_paid_cents: number;
  outstanding_payroll_cents: number;
  grace_accrued_cents: number;
  net_cash_activity_cents: number;
  projected_month_end_net_cents: number;
}

export interface FinancialAuditRow {
  id: string;
  occurred_at: string;
  area: string;
  action: string;
  record_table: string | null;
  record_id: string | null;
  actor_label: string | null;
  reason: string | null;
  previous_value: any;
  new_value: any;
  changed_fields: string[] | null;
}

/** Live financial health. Automatically re-reconciles when something changed. */
export const getFinancialHealth = (force = false) =>
  invoke<FinancialHealth>("health", { force });

export const verifyIntegrity = (range: { start_date?: string; end_date?: string } = {}) =>
  invoke<{ ok: true; checks: IntegrityCheck[]; findings: Record<string, any[]>; verified: boolean; unresolved: number }>(
    "verify",
    range,
  );

export const listPeriodClosures = () =>
  invoke<{ ok: true; closures: PeriodClosure[] }>("list_closures");

export const closeMonth = (month: string, opts: { notes?: string; force?: boolean } = {}) =>
  invoke<{ ok: true; closure: PeriodClosure; verification: any }>("close_month", {
    month, confirmed: true, ...opts,
  });

export const reopenMonth = (closure_id: string, reason: string) =>
  invoke<{ ok: true; closure: PeriodClosure }>("reopen_month", { closure_id, reason, confirmed: true });

export const captureDailySnapshot = (date?: string) =>
  invoke<{ ok: true; snapshot: DailySnapshot }>("daily_snapshot", { date });

export const getSnapshotHistory = (days = 60) =>
  invoke<{ ok: true; snapshots: DailySnapshot[] }>("snapshot_history", { days });

export const getFinancialAuditLog = (filters: { area?: string; start_date?: string; end_date?: string; limit?: number } = {}) =>
  invoke<{ ok: true; rows: FinancialAuditRow[] }>("audit_log", filters);

export const isPeriodClosed = (closures: PeriodClosure[], date: string) =>
  closures.some((c) => c.status === "closed" && date >= c.period_start && date <= c.period_end);


// ---------------------------------------------------------------- formatting
export const money = (cents: number) =>
  `${cents < 0 ? "-" : ""}$${Math.abs(Math.round(cents ?? 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export function formatMethod(method: string | null): string {
  if (!method) return "—";
  return PAYMENT_METHODS.find((m) => m.value === method)?.label ?? method;
}

export function formatStatus(status: LedgerStatus): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function sourceLink(entry: LedgerEntry): { label: string; href: string } | null {
  if (entry.related_registration_id) {
    return { label: "Registration", href: `/admin/registrations?registration=${entry.related_registration_id}` };
  }
  if (entry.related_instructor_id) {
    return { label: "Instructor", href: `/admin/instructors?instructor=${entry.related_instructor_id}` };
  }
  if (entry.source_type === "partner_payment") {
    return { label: "Grace report", href: "/admin/reports" };
  }
  if (entry.related_class_id) {
    return { label: "Class", href: `/admin/classes?class=${entry.related_class_id}` };
  }
  return null;
}

export function toCsv(rows: LedgerEntry[]): string {
  const headers = [
    "Transaction ID", "Date", "Type", "Category", "Description", "Money In", "Money Out",
    "Payment Method", "Status", "Related Instructor", "Related Class", "Related Registration",
    "Source ID", "Notes", "Created By", "Created Date", "Reconciliation Status",
  ];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      r.id, r.transaction_date,
      r.direction === "in" ? "Money In" : r.direction === "out" ? "Money Out" : "Transfer",
      r.category, r.description,
      r.direction === "in" ? (r.amount_cents / 100).toFixed(2) : "",
      r.direction === "out" ? (r.amount_cents / 100).toFixed(2) : "",
      formatMethod(r.payment_method), formatStatus(r.status),
      r.related_instructor_id ?? "", r.related_class_title ?? r.related_class_id ?? "",
      r.related_registration_id ?? "", r.source_id ?? "", r.notes ?? "",
      r.created_by_label ?? "", r.created_at, r.reconciled_at ? "Reconciled" : "Unreconciled",
    ].map(esc).join(","),
  );
  return [headers.join(","), ...lines].join("\n");
}

// ------------------------------------------------- audit & detail reporting
export interface RegistrationReportRow {
  transaction_id: string;
  date: string;
  parent_name: string | null;
  student_name: string | null;
  class_title: string;
  class_id: string;
  amount_cents: number;
  payment_method: string;
  payment_status: string;
  stripe_session_id: string | null;
  refunded_amount_cents: number;
  included_in_revenue: boolean;
  excluded_reason: string | null;
  ledger_entry_id: string | null;
  ledger_amount_cents: number | null;
  ledger_status: string;
  amount_matches: boolean;
  refund_entry_ids: string[];
  reconciled: boolean;
}

export interface RegistrationReport {
  ok: true;
  period: LedgerPeriod;
  rows: RegistrationReportRow[];
  totals: {
    source_record_count: number;
    included_count: number;
    excluded_count: number;
    source_total_cents: number;
    ledger_total_cents: number;
    difference_cents: number;
    refunds_source_cents: number;
    refunds_ledger_cents: number;
    missing_from_ledger: number;
    orphan_entries: Array<{ entry_id: string; description: string; amount_cents: number; source_id: string }>;
  };
}

export interface PayrollReportRow {
  payroll_id: string;
  paid_date: string;
  instructor_id: string | null;
  instructor_name: string;
  pay_period: string;
  classes: string[];
  amount_cents: number;
  effective_cents: number;
  payment_method: string | null;
  payment_destination: string | null;
  reference_number: string | null;
  entry_kind: string;
  voided: boolean;
  ledger_entry_id: string | null;
  ledger_direction: string | null;
  ledger_reversal_id: string | null;
  ledger_effect_cents: number;
  ledger_status: string;
  matches: boolean;
  reconciled: boolean;
  confirmed_by: string | null;
}

export interface PayrollReport {
  ok: true;
  period: LedgerPeriod;
  rows: PayrollReportRow[];
  by_instructor: Array<{ instructor: string; paid_cents: number; payments: number }>;
  totals: {
    source_record_count: number;
    source_total_cents: number;
    ledger_total_cents: number;
    difference_cents: number;
    missing_from_ledger: number;
    mismatched: number;
    orphan_entries: Array<{ entry_id: string; description: string; amount_cents: number; source_id: string }>;
  };
}

export interface AuditReport {
  ok: true;
  period: LedgerPeriod;
  generated_at: string;
  summary: LedgerSummary;
  proof: {
    registrations: RegistrationReport["totals"];
    payroll: PayrollReport["totals"];
    revenue_ties_out: boolean;
    payroll_ties_out: boolean;
  };
  findings: Record<string, any[]>;
  discrepancy_count: number;
  reconciled_through: string | null;
  verdict: string;
}

export interface CardDrilldown {
  ok: true;
  period: LedgerPeriod;
  card: string;
  label: string;
  explain: string;
  rows: LedgerEntry[];
  row_count?: number;
  total_cents: number | null;
  engine_backed: boolean;
}

type Range = { start_date?: string; end_date?: string };

export const getRegistrationReport = (period: string, range?: Range) =>
  invoke<RegistrationReport>("registration_report", { period, ...range });

export const getPayrollReport = (period: string, range?: Range) =>
  invoke<PayrollReport>("payroll_report", { period, ...range });

export const getAuditReport = (period: string, range?: Range) =>
  invoke<AuditReport>("audit_report", { period, ...range });

export const getCardDrilldown = (card: string, period: string, range?: Range) =>
  invoke<CardDrilldown>("card_drilldown", { card, period, ...range });

// ------------------------------------------------------------- CSV builders
const csvEsc = (v: unknown) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function csv(headers: string[], rows: unknown[][], meta: string[] = []): string {
  return [
    ...meta.map((m) => csvEsc(m)),
    headers.join(","),
    ...rows.map((r) => r.map(csvEsc).join(",")),
  ].join("\n");
}

const dollars = (c: number | null | undefined) => (Number(c ?? 0) / 100).toFixed(2);

export function registrationReportCsv(r: RegistrationReport): string {
  return csv(
    ["Transaction ID", "Date", "Parent", "Student", "Class", "Amount", "Payment Method",
      "Payment Status", "Stripe Session", "Refunded", "Counted In Revenue", "Excluded Reason",
      "Ledger Entry ID", "Ledger Amount", "Ledger Status", "Amounts Match", "Reconciled"],
    r.rows.map((x) => [
      x.transaction_id, x.date, x.parent_name, x.student_name, x.class_title, dollars(x.amount_cents),
      x.payment_method, x.payment_status, x.stripe_session_id, dollars(x.refunded_amount_cents),
      x.included_in_revenue ? "Yes" : "No", x.excluded_reason, x.ledger_entry_id,
      x.ledger_amount_cents === null ? "" : dollars(x.ledger_amount_cents),
      x.ledger_status, x.amount_matches ? "Yes" : "No", x.reconciled ? "Reconciled" : "Unreconciled",
    ]),
    [
      `Silver Sage Homeschool Hub — Registration Payments Detail`,
      `Period: ${r.period.label} (${r.period.start_date} to ${r.period.end_date})`,
      `Generated: ${new Date().toLocaleString("en-US")}`,
      `Source records: ${r.totals.source_record_count} · Counted in revenue: ${r.totals.included_count}`,
      `Source total: $${dollars(r.totals.source_total_cents)} · Ledger total: $${dollars(r.totals.ledger_total_cents)} · Difference: $${dollars(r.totals.difference_cents)}`,
      "",
    ],
  );
}

export function payrollReportCsv(r: PayrollReport): string {
  return csv(
    ["Payroll ID", "Paid Date", "Instructor", "Pay Period", "Classes", "Amount", "Effective Amount",
      "Payment Method", "Payment Destination", "Reference", "Entry Kind", "Voided",
      "Ledger Entry ID", "Ledger Effect", "Ledger Status", "Matches", "Confirmed By", "Reconciled"],
    r.rows.map((x) => [
      x.payroll_id, x.paid_date, x.instructor_name, x.pay_period, x.classes.join(" | "),
      dollars(x.amount_cents), dollars(x.effective_cents), x.payment_method, x.payment_destination,
      x.reference_number, x.entry_kind, x.voided ? "Yes" : "No", x.ledger_entry_id,
      dollars(x.ledger_effect_cents), x.ledger_status, x.matches ? "Yes" : "No",
      x.confirmed_by, x.reconciled ? "Reconciled" : "Unreconciled",
    ]),
    [
      `Silver Sage Homeschool Hub — Payroll Payments Detail`,
      `Period: ${r.period.label} (${r.period.start_date} to ${r.period.end_date})`,
      `Generated: ${new Date().toLocaleString("en-US")}`,
      `Source records: ${r.totals.source_record_count}`,
      `Source total: $${dollars(r.totals.source_total_cents)} · Ledger total: $${dollars(r.totals.ledger_total_cents)} · Difference: $${dollars(r.totals.difference_cents)}`,
      "",
    ],
  );
}

export function auditReportCsv(a: AuditReport): string {
  const lines: string[] = [
    csvEsc("Silver Sage Homeschool Hub — Financial Audit Report"),
    csvEsc(`Period: ${a.period.label} (${a.period.start_date} to ${a.period.end_date})`),
    csvEsc(`Generated: ${new Date().toLocaleString("en-US")}`),
    csvEsc(a.verdict),
    "",
    "Figure,Amount,Traces To",
    `Actual Revenue,${dollars(a.summary.actual_revenue_cents)},Registration payments detail`,
    `Actual Expenses,${dollars(a.summary.actual_expenses_cents)},Payroll + refunds + other money out`,
    `Net Cash Activity,${dollars(a.summary.net_cash_activity_cents)},Money in minus money out`,
    `Payroll Paid,${dollars(a.summary.payroll_paid_cents)},Payroll payments detail`,
    `Refunds,${dollars(a.summary.refunds_cents)},Refund transactions`,
    `Grace Paid,${dollars(a.summary.grace_paid_cents)},Partner payment records`,
    "",
    "Proof,Source Total,Ledger Total,Difference",
    `Registrations,${dollars(a.proof.registrations.source_total_cents)},${dollars(a.proof.registrations.ledger_total_cents)},${dollars(a.proof.registrations.difference_cents)}`,
    `Payroll,${dollars(a.proof.payroll.source_total_cents)},${dollars(a.proof.payroll.ledger_total_cents)},${dollars(a.proof.payroll.difference_cents)}`,
    "",
    "Finding,Count,Detail",
  ];
  for (const [key, rows] of Object.entries(a.findings)) {
    lines.push([key.replace(/_/g, " "), String(rows.length), rows.length ? JSON.stringify(rows) : "None"].map(csvEsc).join(","));
  }
  return lines.join("\n");
}

export function downloadCsv(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------- bank reconciliation

export type BankMatchStatus = "matched" | "possible" | "unmatched" | "ignored";

export interface BankStatementImport {
  id: string;
  label: string;
  file_name: string | null;
  source_format: string;
  account_label: string | null;
  statement_start: string | null;
  statement_end: string | null;
  opening_balance_cents: number | null;
  closing_balance_cents: number | null;
  row_count: number;
  imported_by_label: string | null;
  notes: string | null;
  created_at: string;
}

export interface BankCandidate {
  entry_id: string;
  transaction_ref: string | null;
  transaction_date: string;
  description: string;
  category: string;
  counterparty_name: string | null;
  amount_cents: number;
  direction: string;
  score: number;
  reason: string;
}

export interface BankTransactionRow {
  id: string;
  import_id: string;
  posted_date: string;
  description: string;
  amount_cents: number;
  direction: "in" | "out";
  bank_reference: string | null;
  channel: string;
  match_status: BankMatchStatus;
  match_confidence: number;
  match_reason: string | null;
  matched_entry_id: string | null;
  matched_transaction_ref: string | null;
  candidate_entry_ids: BankCandidate[];
  confirmed_by_label: string | null;
  confirmed_at: string | null;
  matched_entry?: LedgerEntry | null;
}

export interface BankReconciliationCause {
  kind: "missing_from_ledger" | "missing_from_bank" | "possible";
  date: string;
  description: string;
  amount_cents: number;
  effect_cents: number;
  transaction_ref: string | null;
  detail: string;
}

export interface BankReconciliation {
  ok: true;
  statement: BankStatementImport;
  period: { start_date: string; end_date: string };
  totals: {
    bank_balance_cents: number;
    ledger_balance_cents: number;
    difference_cents: number;
    bank_net_cents: number;
    ledger_net_cents: number;
    opening_balance_cents: number | null;
    matched_count: number;
    possible_count: number;
    missing_from_ledger_count: number;
    missing_from_bank_count: number;
    ignored_count: number;
    bank_line_count: number;
    ledger_line_count: number;
  };
  matched: BankTransactionRow[];
  possible: BankTransactionRow[];
  missing_from_ledger: BankTransactionRow[];
  missing_from_bank: LedgerEntry[];
  ignored: BankTransactionRow[];
  causes: BankReconciliationCause[];
  balanced: boolean;
}

export const importBankStatement = (payload: {
  file_text: string;
  file_name?: string;
  format?: string;
  label?: string;
  account_label?: string;
  statement_start?: string;
  statement_end?: string;
  opening_balance_cents?: number | null;
  closing_balance_cents?: number | null;
}) =>
  invoke<{ ok: true; import_id: string; imported: number; skipped: string[]; matched: number; possible: number; unmatched: number }>(
    "bank_import",
    payload,
  );

export const listBankImports = () =>
  invoke<{ ok: true; imports: BankStatementImport[] }>("bank_list_imports");

export const deleteBankImport = (import_id: string) =>
  invoke<{ ok: true }>("bank_delete_import", { import_id });

export const rematchBankImport = (import_id: string) =>
  invoke<{ ok: true; matched: number; possible: number; unmatched: number }>("bank_rematch", { import_id });

export const getBankReconciliation = (import_id: string) =>
  invoke<BankReconciliation>("bank_reconciliation", { import_id });

export const confirmBankMatch = (bank_transaction_id: string, entry_id: string) =>
  invoke<{ ok: true }>("bank_confirm_match", { bank_transaction_id, entry_id });

export const unmatchBankTransaction = (bank_transaction_id: string) =>
  invoke<{ ok: true }>("bank_unmatch", { bank_transaction_id });

export const ignoreBankTransaction = (bank_transaction_id: string, reason?: string, undo?: boolean) =>
  invoke<{ ok: true }>("bank_ignore", { bank_transaction_id, reason, undo });

/** CSV export of a full bank reconciliation, including the difference causes. */
export function bankReconciliationCsv(r: BankReconciliation): string {
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines: string[] = [];
  lines.push(esc(`Silver Sage Homeschool Hub — Bank Reconciliation`));
  lines.push(esc(`Statement: ${r.statement.label}`));
  lines.push(esc(`Period: ${r.period.start_date} to ${r.period.end_date}`));
  lines.push(esc(`Generated: ${new Date().toISOString()}`));
  lines.push(esc(`Bank Balance: ${money(r.totals.bank_balance_cents)}`));
  lines.push(esc(`Ledger Balance: ${money(r.totals.ledger_balance_cents)}`));
  lines.push(esc(`Difference: ${money(r.totals.difference_cents)}`));
  lines.push("");
  lines.push(["Status", "Date", "Description", "Amount", "Direction", "Transaction ID", "Detail"].map(esc).join(","));
  const push = (status: string, rows: BankTransactionRow[]) => {
    rows.forEach((b) =>
      lines.push([status, b.posted_date, b.description, money(Math.abs(b.amount_cents)), b.direction === "in" ? "Deposit" : "Withdrawal", b.matched_transaction_ref ?? "", b.match_reason ?? ""].map(esc).join(",")),
    );
  };
  push("Matched", r.matched);
  push("Possible match", r.possible);
  push("Missing from ledger", r.missing_from_ledger);
  push("Ignored", r.ignored);
  r.missing_from_bank.forEach((e) =>
    lines.push(["Missing from bank", e.transaction_date, e.description || e.category, money(e.amount_cents), e.direction === "in" ? "Money in" : "Money out", (e as unknown as { transaction_ref?: string }).transaction_ref ?? "", "In the ledger but not on this statement"].map(esc).join(",")),
  );
  return lines.join("\n");
}
